import { useEffect, useState } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { useAuth } from '@/context/AuthContext';
import { MediaUpload } from '@/components/MediaUpload';
import { ArrowLeft, Loader2, Save } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const VISIBILITY_FIELDS = [
  { key: 'show_total_price', label: 'Show Total Price' },
  { key: 'show_rate_per_sqft', label: 'Show Price / sq ft' },
  { key: 'show_gov_rate', label: 'Show Government Value' },
  { key: 'show_area', label: 'Show Area' },
  { key: 'show_price_range', label: 'Show Price Range / "Contact" CTA' },
];

const ICON_OPTIONS = ['Maximize2', 'Home', 'Building2', 'Trees', 'Warehouse', 'Store', 'LandPlot', 'Mountain', 'Map'];

const blank = {
  slug: '',
  name_en: '',
  name_hi: '',
  short_description_en: '',
  short_description_hi: '',
  icon: 'Building2',
  thumbnail_url: '',
  hero_image: '',
  order: 99,
  is_active: true,
  default_visibility: {
    show_total_price: true,
    show_rate_per_sqft: true,
    show_gov_rate: true,
    show_area: true,
    show_price_range: true,
  },
  meta_title: '',
  meta_description: '',
};

export default function CategoryForm() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { token } = useAuth();
  const isEdit = Boolean(id);
  const [data, setData] = useState(blank);
  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);

  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    if (!isEdit) return;
    (async () => {
      try {
        const { data: list } = await axios.get(`${API}/categories`);
        const found = list.find((c) => c.id === id);
        if (!found) throw new Error('Not found');
        setData({ ...blank, ...found, default_visibility: { ...blank.default_visibility, ...(found.default_visibility || {}) } });
      } catch {
        toast.error('Failed to load category');
        navigate('/dashboard/categories');
      } finally {
        setLoading(false);
      }
    })();
  }, [id]);

  const update = (k, v) => setData((d) => ({ ...d, [k]: v }));
  const updateVis = (k, v) => setData((d) => ({ ...d, default_visibility: { ...d.default_visibility, [k]: v } }));

  const submit = async (e) => {
    e.preventDefault();
    if (!data.name_en) return toast.error('Name (English) is required');
    setSaving(true);
    try {
      const payload = { ...data };
      // Strip empty Hindi fields so backend GPT-5.2 auto-translates them
      if (!payload.name_hi) delete payload.name_hi;
      if (!payload.short_description_hi) delete payload.short_description_hi;
      if (isEdit) {
        await axios.put(`${API}/categories/${id}`, payload, { headers });
      } else {
        await axios.post(`${API}/categories`, payload, { headers });
      }
      toast.success('Saved. Hindi auto-translated by AI when empty.');
      navigate('/dashboard/categories');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="flex justify-center py-20"><Loader2 className="h-7 w-7 animate-spin text-brand-orange" /></div>;
  }

  return (
    <form onSubmit={submit} className="space-y-6 max-w-4xl" data-testid="category-form">
      <div className="flex items-center justify-between">
        <Link to="/dashboard/categories" className="text-gray-600 hover:text-brand-orange flex items-center text-sm">
          <ArrowLeft className="h-4 w-4 mr-1" /> Back to categories
        </Link>
        <Button type="submit" disabled={saving} className="bg-gradient-to-r from-brand-orange to-brand-rust text-white" data-testid="save-category-btn">
          {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Save className="h-4 w-4 mr-2" />}
          {isEdit ? 'Update' : 'Create'} Category
        </Button>
      </div>

      <h1 className="font-heading text-2xl md:text-3xl font-bold text-gray-900">
        {isEdit ? 'Edit Category' : 'New Category'}
      </h1>

      <Card>
        <CardHeader><CardTitle>Basics</CardTitle></CardHeader>
        <CardContent className="grid gap-4 md:grid-cols-2">
          <div className="space-y-2">
            <Label>Slug (URL identifier)</Label>
            <Input value={data.slug} onChange={(e) => update('slug', e.target.value)} placeholder="e.g. farmhouse" />
            <p className="text-xs text-gray-500">Lowercase, hyphens only. Auto-generated if left empty.</p>
          </div>
          <div className="space-y-2">
            <Label>Display Order</Label>
            <Input type="number" value={data.order} onChange={(e) => update('order', Number(e.target.value))} />
          </div>
          <div className="space-y-2">
            <Label>Name (English) *</Label>
            <Input value={data.name_en} onChange={(e) => update('name_en', e.target.value)} required data-testid="cat-name-en" />
          </div>
          <div className="space-y-2">
            <Label>Name (Hindi)</Label>
            <Input value={data.name_hi || ''} onChange={(e) => update('name_hi', e.target.value)} placeholder="e.g. फार्महाउस" />
          </div>
          <div className="space-y-2 md:col-span-2">
            <Label>Short Description (English)</Label>
            <Textarea rows={2} value={data.short_description_en || ''} onChange={(e) => update('short_description_en', e.target.value)} />
          </div>
          <div className="space-y-2 md:col-span-2">
            <Label>Short Description (Hindi)</Label>
            <Textarea rows={2} value={data.short_description_hi || ''} onChange={(e) => update('short_description_hi', e.target.value)} />
          </div>
          <div className="space-y-2">
            <Label>Icon (lucide-react name)</Label>
            <select value={data.icon} onChange={(e) => update('icon', e.target.value)} className="w-full border rounded-md px-3 py-2">
              {ICON_OPTIONS.map((i) => <option key={i} value={i}>{i}</option>)}
            </select>
          </div>
          <div className="flex items-center gap-3 pt-7">
            <Switch checked={data.is_active} onCheckedChange={(v) => update('is_active', v)} id="active" />
            <Label htmlFor="active">Active (show on site)</Label>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader><CardTitle>Visuals</CardTitle></CardHeader>
        <CardContent className="space-y-6">
          <div>
            <Label className="mb-2 block">Card Thumbnail (square, ~800×800)</Label>
            <MediaUpload kind="image" value={data.thumbnail_url} onChange={(v) => update('thumbnail_url', v)} testId="cat-thumb" />
          </div>
          <div>
            <Label className="mb-2 block">Listing Page Banner (wide, ~1600×900)</Label>
            <MediaUpload kind="image" value={data.hero_image} onChange={(v) => update('hero_image', v)} testId="cat-hero" />
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Default Field Visibility</CardTitle>
          <p className="text-sm text-gray-500">Controls what is shown for properties under this category. Each listing can override these.</p>
        </CardHeader>
        <CardContent className="grid gap-3 md:grid-cols-2">
          {VISIBILITY_FIELDS.map((f) => (
            <div key={f.key} className="flex items-center justify-between border rounded-lg px-3 py-2">
              <Label htmlFor={f.key}>{f.label}</Label>
              <Switch
                id={f.key}
                checked={Boolean(data.default_visibility?.[f.key])}
                onCheckedChange={(v) => updateVis(f.key, v)}
                data-testid={`vis-${f.key}`}
              />
            </div>
          ))}
        </CardContent>
      </Card>

      <Card>
        <CardHeader><CardTitle>SEO</CardTitle></CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label>Meta Title</Label>
            <Input value={data.meta_title || ''} onChange={(e) => update('meta_title', e.target.value)} />
          </div>
          <div className="space-y-2">
            <Label>Meta Description</Label>
            <Textarea rows={3} value={data.meta_description || ''} onChange={(e) => update('meta_description', e.target.value)} />
          </div>
        </CardContent>
      </Card>
    </form>
  );
}
